import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import LinkItem from '@atlaskit/menu/link-item';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	getDevDocsRepositoryUrl,
	getDevDocsSourceProviderConfigFromValue,
	getDevDocsSourceProviderDisplayName,
} from '../source-resolution/devDocsSourceProviders.ts';
import { formatDevDocsRelativeSyncTime } from '../folder-sync/devDocsSyncStatusPresentation.ts';
import { DevDocsScmProviderLogo } from '../DevDocsScmProviderIcons.tsx';

import type { DevDocsBylineConnection } from './devDocsConnections.ts';

const i18n = defineMessages({
	// Message id retained from the byline menu section so existing translations
	// carry over now that the row is shared with the preview panel.
	providerRepo: {
		id: 'dev-docs.byline-connection.menu.section.provider-repo.ai-non-final',
		defaultMessage: '{provider} repo',
		description:
			'Metadata label in the Teamwork Graph byline menu for a Dev Docs source repository. The provider placeholder is the source control provider name, such as GitHub or Bitbucket.',
	},
});

const styles = cssMap({
	rowWrapper: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
	},
	textColumn: {
		minWidth: 0,
		flex: 1,
	},
	title: {
		font: token('font.body'),
		fontWeight: token('font.weight.regular'),
		color: token('color.text'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	metadata: {
		minWidth: 0,
		font: token('font.body.small'),
		fontWeight: token('font.weight.regular'),
		color: token('color.text.subtlest'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	metadataPart: {
		minWidth: 0,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	metadataSeparator: {
		display: 'inline-block',
		flexShrink: 0,
		width: '3px',
		height: '3px',
		marginInline: token('space.050'),
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.neutral.bold'),
	},
});

type LinkItemWithInteractionProps = React.ComponentProps<typeof LinkItem> &
	Pick<
		React.AnchorHTMLAttributes<HTMLAnchorElement>,
		'onBlur' | 'onFocus' | 'onMouseEnter' | 'onMouseLeave'
	>;

const LinkItemWithInteraction = LinkItem as React.ComponentType<LinkItemWithInteractionProps>;

/**
 * A single Dev Docs source row. Falls back to a non-interactive row when the
 * repository URL cannot be resolved.
 *
 * Shared by the Teamwork Graph byline menu and the preview panel, so it renders no
 * section heading of its own.
 */
export const DevDocsConnectionRow = ({
	connection,
	onNavigate,
}: {
	connection: DevDocsBylineConnection;
	onNavigate?: () => void;
}): React.JSX.Element => {
	const intl = useIntl();
	const { formatMessage } = intl;
	const { environment } = useSessionData();
	const [isRepositoryLinkIconVisible, setIsRepositoryLinkIconVisible] = useState(false);
	const lastSynced = formatDevDocsRelativeSyncTime(connection.lastSynced, intl);
	const providerConfig = getDevDocsSourceProviderConfigFromValue(connection.provider);
	const providerDisplayName =
		providerConfig?.displayName ?? getDevDocsSourceProviderDisplayName(connection.provider);
	const repositoryUrl = getDevDocsRepositoryUrl({
		environment,
		provider: connection.provider,
		repoName: connection.repoName,
	});
	const providerTypeLabel = providerDisplayName
		? formatMessage(i18n.providerRepo, { provider: providerDisplayName })
		: null;
	const metadataParts = [providerTypeLabel, connection.owner, lastSynced].filter(
		(metadataPart): metadataPart is string => Boolean(metadataPart),
	);
	const metadata = metadataParts.length ? (
		<Inline as="span" alignBlock="center" xcss={styles.metadata}>
			{metadataParts.map((metadataPart, index) => (
				<React.Fragment key={`${metadataPart}-${index}`}>
					{index > 0 ? <Box as="span" xcss={styles.metadataSeparator} aria-hidden="true" /> : null}
					<Box as="span" xcss={styles.metadataPart}>
						{metadataPart}
					</Box>
				</React.Fragment>
			))}
		</Inline>
	) : null;
	const rowText = (
		<Stack xcss={styles.textColumn} space="space.025">
			<Box xcss={styles.title}>{connection.repoName}</Box>
			{metadata}
		</Stack>
	);
	const showRepositoryLinkIcon = useCallback(() => {
		setIsRepositoryLinkIconVisible(true);
	}, []);
	const hideRepositoryLinkIcon = useCallback(() => {
		setIsRepositoryLinkIconVisible(false);
	}, []);

	if (!repositoryUrl) {
		return (
			<Box xcss={styles.rowWrapper}>
				<Inline space="space.150" alignBlock="center">
					<DevDocsScmProviderLogo provider={connection.provider} />
					{rowText}
				</Inline>
			</Box>
		);
	}

	return (
		<LinkItemWithInteraction
			href={repositoryUrl}
			target="_blank"
			rel="noopener noreferrer"
			onClick={onNavigate}
			onMouseEnter={showRepositoryLinkIcon}
			onMouseLeave={hideRepositoryLinkIcon}
			onFocus={showRepositoryLinkIcon}
			onBlur={hideRepositoryLinkIcon}
			iconBefore={<DevDocsScmProviderLogo provider={connection.provider} />}
			iconAfter={
				isRepositoryLinkIconVisible ? (
					<GrowDiagonalIcon
						label=""
						color={token('color.icon.subtle')}
						testId="dev-docs-byline-repository-link-icon"
					/>
				) : null
			}
		>
			{rowText}
		</LinkItemWithInteraction>
	);
};
